import React, { useState } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase-config';

const LoginPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState('coach');
  const [coachCode, setCoachCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generateCoachCode = () => {
    const adjectives = ['swift', 'power', 'elite', 'peak', 'prime', 'apex', 'force', 'tempo'];
    const numbers = Math.floor(Math.random() * 100);
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    return `${adjective}${numbers}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        // Login
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        // Get user data from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        
        onLogin({ ...user, ...userData });
      } else {
        // Register
        if (!name.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }

	

        if (accountType === 'athlete' && !coachCode.trim()) {
          setError('Please enter your coach\'s code');
          setLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Prepare user data
        const userData = {
          email: user.email,
          name: name.trim(),
          created_at: new Date()
        };

        if (accountType === 'coach') {
          userData.role = 'coach';
          userData.plan = 'coach';
          userData.coach_code = generateCoachCode();
        } else if (accountType === 'self') {
          userData.role = 'coach';
          userData.plan = 'athlete';
          userData.coach_code = generateCoachCode();
        } else if (accountType === 'athlete') {
          userData.role = 'athlete';
          const { findCoachByCode } = await import('./auth');
          const coachResult = await findCoachByCode(coachCode.trim());
          if (!coachResult.success) {
            setError('Coach code not found. Please check with your coach.');
            setLoading(false);
            return;
          }
          userData.coach_id = coachResult.coach.uid;
          userData.coach_code = coachCode.trim().toLowerCase();
        }

        // Save user data to Firestore
        await setDoc(doc(db, 'users', user.uid), userData);

        onLogin({ ...user, ...userData });
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError(error.message);
    }

    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f8fafc 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '48px 32px',
        border: '1px solid rgba(37, 99, 235, 0.1)',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        
        {/* Logo Section */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 10px 25px rgba(37, 99, 235, 0.2)'
          }}>
            <div style={{ color: 'white', fontSize: '32px' }}>⏱</div>
          </div>
          
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            color: '#1e293b',
            margin: '0 0 8px 0',
            fontFamily: '"Inter", sans-serif',
            letterSpacing: '-0.02em'
          }}>
            <span style={{ color: '#2563eb' }}>COACH'S</span> <span style={{ color: '#1e293b' }}>LOG</span>
          </h1>
          
          <p style={{
            color: '#64748b',
            fontSize: '16px',
            fontWeight: '500',
            margin: 0,
            letterSpacing: '0.025em'
          }}>
            TRAINING JOURNAL
          </p>
          
          <div style={{
            height: '3px',
            width: '60px',
            background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
            margin: '16px auto 0',
            borderRadius: '2px'
          }} />
        </div>

        {/* Welcome Message */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px',
          padding: '16px',
          background: 'rgba(37, 99, 235, 0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(37, 99, 235, 0.1)'
        }}>
          <h2 style={{
            color: '#1e293b',
            fontSize: '18px',
            fontWeight: '600',
            margin: '0 0 4px 0',
            letterSpacing: '-0.01em'
          }}>
            Welcome {isLogin ? 'Back' : 'to Coach\'s Log'}
          </h2>
          <p style={{
            color: '#64748b',
            fontSize: '14px',
            margin: 0,
            fontWeight: '500'
          }}>
            {isLogin ? 'Sign in to your account' : 'Create your account to get started'}
          </p>
        </div>

        {/* Toggle Buttons */}
        <div style={{
          display: 'flex',
          marginBottom: '24px',
          background: '#f1f5f9',
          borderRadius: '12px',
          padding: '4px',
          border: '1px solid #e2e8f0'
        }}>
          <button
            onClick={() => setIsLogin(true)}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderRadius: '8px',
              background: isLogin ? 'linear-gradient(135deg, #2563eb, #3b82f6)' : 'transparent',
              color: isLogin ? 'white' : '#64748b',
              fontWeight: '600',
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            LOGIN
          </button>
          <button
            onClick={() => setIsLogin(false)}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderRadius: '8px',
              background: !isLogin ? 'linear-gradient(135deg, #2563eb, #3b82f6)' : 'transparent',
              color: !isLogin ? 'white' : '#64748b',
              fontWeight: '600',
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            REGISTER
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                color: '#374151',
                fontWeight: '600',
                fontSize: '14px',
                marginBottom: '8px'
              }}>
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '12px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#1e293b',
                  fontSize: '14px',
                  fontWeight: '500',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontWeight: '600',
              fontSize: '14px',
              marginBottom: '8px'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '12px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                color: '#1e293b',
                fontSize: '14px',
                fontWeight: '500',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontWeight: '600',
              fontSize: '14px',
              marginBottom: '8px'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '12px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                color: '#1e293b',
                fontSize: '14px',
                fontWeight: '500',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {!isLogin && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  color: '#374151',
                  fontWeight: '600',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}>
                  I am a...
                </label>
                <select
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#1e293b',
                    fontSize: '14px',
                    fontWeight: '500',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="coach">I'm a coach</option>
                  <option value="self">I train myself</option>
                  <option value="athlete">I have a coach</option>
                </select>
              </div>

              {accountType === 'athlete' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}>
                    Coach Code
                  </label>
                  <input
                    type="text"
                    value={coachCode}
                    onChange={(e) => setCoachCode(e.target.value)}
                    placeholder="Your coach's code (e.g., TEMPO47)"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '12px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      color: '#1e293b',
                      fontSize: '14px',
                      fontWeight: '500',
                      outline: 'none',
                      textTransform: 'uppercase',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              )}
            </>
          )}

          {error && (
            <div style={{
              padding: '12px',
              marginBottom: '20px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              border: 'none',
              borderRadius: '12px',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, #2563eb, #3b82f6)',
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: loading ? 'none' : '0 4px 14px rgba(37, 99, 235, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 8px 25px rgba(37, 99, 235, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 14px rgba(37, 99, 235, 0.3)';
              }
            }}
          >
            {loading ? 'Processing...' : (isLogin ? '🚀 Sign In' : '⚡ Create Account')}
          </button>
        </form>

        <div style={{
          textAlign: 'center',
          marginTop: '24px',
          color: '#64748b',
          fontSize: '12px',
          fontWeight: '500'
        }}>
          Professional training management for coaches & athletes
        </div>
      </div>
    </div>
  );
};

export default LoginPage;